import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarCheck, Plus, CheckCircle, XCircle, Clock, Stethoscope, MapPin, Trash2, Paperclip, Upload, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';

const tipiVisitaLabels = {
  prelievo_ematico: 'Prelievo Ematico', visita_cardiologica: 'Visita Cardiologica',
  visita_ortopedica: 'Visita Ortopedica', visita_neurologica: 'Visita Neurologica',
  visita_dermatologica: 'Visita Dermatologica', visita_oculistica: 'Visita Oculistica',
  visita_odontoiatrica: 'Visita Odontoiatrica', visita_urologica: 'Visita Urologica',
  visita_ginecologica: 'Visita Ginecologica', visita_pneumologica: 'Visita Pneumologica',
  visita_diabetologica: 'Visita Diabetologica', visita_geriatrica: 'Visita Geriatrica',
  controllo_mmg: 'Controllo MMG', esame_diagnostico: 'Esame Diagnostico',
  ecografia: 'Ecografia', radiografia: 'Radiografia', tac: 'TAC',
  risonanza: 'Risonanza Magnetica', fisioterapia: 'Fisioterapia',
  logopedia: 'Logopedia', altro: 'Altro',
};

const statoMap = {
  programmato: { label: 'Programmato', class: 'bg-blue-100 text-blue-700', icon: Clock },
  effettuato: { label: 'Effettuato', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  annullato: { label: 'Annullato', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export function AppuntamentiOspiteTab({ ospiteId }) {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showConferma, setShowConferma] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [allegati, setAllegati] = useState([]);
  const [showAllegati, setShowAllegati] = useState(false);
  const [allegFile, setAllegFile] = useState(null);
  const [allegNome, setAllegNome] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ data_ora: '', tipo: 'prelievo_ematico', specialista: '', luogo: '', luogo_tipo: 'interno', note: '' });
  const [confermaForm, setConfermaForm] = useState({ esito: '', note_esito: '' });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/appuntamenti', { params: { ospite_id: ospiteId } });
      setItems(res.data);
    } catch { setItems([]); }
  }, [ospiteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.data_ora || !form.tipo) { toast.error('Compila data/ora e tipo visita'); return; }
    try {
      await api.post('/appuntamenti', { ...form, ospite_id: ospiteId });
      toast.success('Appuntamento programmato');
      setShowForm(false);
      setForm({ data_ora: '', tipo: 'prelievo_ematico', specialista: '', luogo: '', luogo_tipo: 'interno', note: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleConferma = async () => {
    try {
      await api.put(`/appuntamenti/${selectedApp.id}/conferma`, confermaForm);
      toast.success('Visita confermata');
      setShowConferma(false);
      fetchData();
    } catch { toast.error('Errore'); }
  };

  const handleAnnulla = async (id) => {
    try { await api.put(`/appuntamenti/${id}`, { stato: 'annullato' }); toast.success('Annullato'); fetchData(); } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo appuntamento?')) return;
    try { await api.delete(`/appuntamenti/${id}`); toast.success('Eliminato'); fetchData(); } catch { toast.error('Errore'); }
  };

  const openAllegati = async (app) => {
    setSelectedApp(app);
    setShowAllegati(true);
    try { const res = await api.get(`/appuntamenti/${app.id}/allegati`); setAllegati(res.data); } catch { setAllegati([]); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || file.size > 10 * 1024 * 1024) return;
    const reader = new FileReader();
    reader.onload = () => { setAllegFile(reader.result); setAllegNome(file.name); };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!allegFile || !selectedApp) return;
    setUploading(true);
    try {
      await api.post(`/appuntamenti/${selectedApp.id}/allegati`, { nome_file: allegNome, tipo: 'referto', contenuto_base64: allegFile });
      toast.success('Allegato caricato');
      setAllegFile(null); setAllegNome('');
      const res = await api.get(`/appuntamenti/${selectedApp.id}/allegati`); setAllegati(res.data);
    } catch { toast.error('Errore'); }
    setUploading(false);
  };

  const handleDownload = async (id, nome) => {
    try { const res = await api.get(`/appuntamenti/allegati/${id}`); const a = document.createElement('a'); a.href = res.data.contenuto_base64; a.download = nome; a.click(); } catch { toast.error('Errore'); }
  };

  const handleDeleteAllegato = async (allegatoId) => {
    if (!selectedApp) return;
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;
    try {
      await api.delete(`/appuntamenti/allegati/${allegatoId}`);
      toast.success('Allegato eliminato');
      const res = await api.get(`/appuntamenti/${selectedApp.id}/allegati`);
      setAllegati(res.data);
    } catch { toast.error('Errore eliminazione'); }
  };

  const prossimi = items.filter(a => a.stato === 'programmato');
  const passati = items.filter(a => a.stato !== 'programmato');

  return (
    <div className="space-y-4" data-testid="appuntamenti-ospite-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-semibold text-slate-800">Appuntamenti</h3>
          <Badge variant="outline" className="text-[10px]">{items.length} totali</Badge>
          {prossimi.length > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{prossimi.length} in programma</Badge>}
        </div>
        {hasRole('coordinatore', 'medico', 'infermiere') && (
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-app-ospite-btn" className="bg-brand hover:bg-brand-dark text-white h-8 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" />Nuovo
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8"><CalendarCheck className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-xs text-slate-400">Nessun appuntamento</p></div>
      ) : (
        <div className="space-y-2">
          {prossimi.length > 0 && <p className="text-[10px] font-semibold text-brand uppercase tracking-wider">Prossimi</p>}
          {prossimi.map(a => {
            const dt = new Date(a.data_ora);
            return (
              <Card key={a.id} className="border-l-2 border-l-blue-400" data-testid={`app-ospite-${a.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-center min-w-[50px]">
                    <p className="text-lg font-bold text-slate-800 leading-none">{dt.getDate()}</p>
                    <p className="text-[10px] text-slate-500 uppercase">{dt.toLocaleDateString('it-IT', { month: 'short' })}</p>
                    <p className="text-xs font-bold text-brand">{dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]"><Stethoscope className="w-3 h-3 mr-0.5" />{tipiVisitaLabels[a.tipo] || a.tipo}</Badge>
                      {a.specialista && <span className="text-xs text-slate-500">{a.specialista}</span>}
                      {a.luogo && <span className="text-xs text-slate-400">{a.luogo}</span>}
                      {a.luogo_tipo === 'esterno' && <Badge className="text-[9px] bg-purple-100 text-purple-600"><MapPin className="w-2.5 h-2.5 mr-0.5" />EST</Badge>}
                    </div>
                    {a.note && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{a.note}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => { setSelectedApp(a); setConfermaForm({ esito: '', note_esito: '' }); setShowConferma(true); }}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />Conferma
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleAnnulla(a.id)}>
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300" onClick={() => handleDelete(a.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {passati.length > 0 && <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-4">Storico</p>}
          {passati.map(a => {
            const dt = new Date(a.data_ora);
            const s = statoMap[a.stato] || statoMap.annullato;
            return (
              <Card key={a.id} className={`${a.stato === 'effettuato' ? 'border-l-2 border-l-emerald-400 bg-emerald-50/20' : 'opacity-60'}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-center min-w-[50px]">
                    <p className="text-sm font-bold text-slate-500 leading-none">{dt.getDate()}</p>
                    <p className="text-[10px] text-slate-400 uppercase">{dt.toLocaleDateString('it-IT', { month: 'short' })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] ${s.class}`}>{s.label}</Badge>
                      <span className="text-xs font-medium">{tipiVisitaLabels[a.tipo] || a.tipo}</span>
                      {a.specialista && <span className="text-xs text-slate-400">{a.specialista}</span>}
                    </div>
                    {a.esito && <p className="text-[10px] text-emerald-600 mt-0.5">{a.esito}</p>}
                    {a.note_esito && <p className="text-[10px] text-slate-400 mt-0.5">{a.note_esito}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {a.stato === 'effettuato' && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-brand" onClick={() => openAllegati(a)} data-testid={`allegati-app-${a.id}`}>
                        <Paperclip className="w-3.5 h-3.5 mr-1" />Allegati
                      </Button>
                    )}
                    {hasRole('coordinatore', 'medico') && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300" onClick={() => handleDelete(a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form nuovo */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-brand" />Nuovo Appuntamento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Data e Ora *</Label><Input type="datetime-local" value={form.data_ora} onChange={e => setForm(f => ({ ...f, data_ora: e.target.value }))} /></div>
              <div><Label className="text-xs">Tipo Visita *</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(tipiVisitaLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Specialista</Label><Input value={form.specialista} onChange={e => setForm(f => ({ ...f, specialista: e.target.value }))} placeholder="Dr. Rossi" /></div>
              <div><Label className="text-xs">Luogo</Label><Input value={form.luogo} onChange={e => setForm(f => ({ ...f, luogo: e.target.value }))} placeholder="Ambulatorio, Ospedale..." /></div>
            </div>
            <div><Label className="text-xs">Tipo Luogo</Label>
              <Select value={form.luogo_tipo} onValueChange={v => setForm(f => ({ ...f, luogo_tipo: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="interno">Interno (in struttura)</SelectItem><SelectItem value="esterno">Esterno (fuori struttura)</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Note</Label><DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button onClick={handleCreate} className="bg-brand hover:bg-brand-dark text-white">Programma</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog allegati */}
      <Dialog open={showAllegati} onOpenChange={setShowAllegati}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip className="w-5 h-5 text-brand" />Allegati Appuntamento</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-semibold">{tipiVisitaLabels[selectedApp.tipo] || selectedApp.tipo}</p>
                <p className="text-xs text-slate-500">{new Date(selectedApp.data_ora).toLocaleString('it-IT')}</p>
                {selectedApp.esito && <p className="text-xs text-emerald-600 mt-1">{selectedApp.esito}</p>}
              </div>

              {/* Lista allegati */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />Documenti ({allegati.length})
                </h4>
                {allegati.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Nessun allegato</p>
                ) : (
                  <div className="space-y-2">
                    {allegati.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 border rounded-lg bg-white" data-testid={`allegato-ospite-${a.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{a.nome_file}</p>
                            <p className="text-[10px] text-slate-400">{a.tipo} - {a.uploaded_by_name} - {new Date(a.uploaded_at).toLocaleDateString('it-IT')}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(a.id, a.nome_file)} data-testid={`download-alleg-ospite-${a.id}`}>
                            <Download className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                          {hasRole('coordinatore', 'medico') && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteAllegato(a.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload */}
              {hasRole('coordinatore', 'medico', 'infermiere') && (
                <div className="border-t pt-3">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Upload className="w-3.5 h-3.5" />Carica Allegato
                  </h4>
                  <div className="space-y-2">
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileSelect} data-testid="allegato-ospite-file-input" className="text-xs" />
                    {allegFile && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{allegNome}</Badge>
                        <Button size="sm" onClick={handleUpload} disabled={uploading} data-testid="upload-allegato-ospite-btn" className="bg-brand hover:bg-brand-dark text-white h-7 text-xs">
                          <Upload className="w-3.5 h-3.5 mr-1" />{uploading ? 'Caricamento...' : 'Carica'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog conferma */}
      <Dialog open={showConferma} onOpenChange={setShowConferma}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" />Conferma Visita</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-semibold">{tipiVisitaLabels[selectedApp.tipo]} - {new Date(selectedApp.data_ora).toLocaleString('it-IT')}</p>
              </div>
              <div><Label className="text-xs">Esito</Label><DictableTextarea value={confermaForm.esito} onChange={e => setConfermaForm(f => ({ ...f, esito: e.target.value }))} rows={2} placeholder="Esito della visita..." /></div>
              <div><Label className="text-xs">Note</Label><DictableTextarea value={confermaForm.note_esito} onChange={e => setConfermaForm(f => ({ ...f, note_esito: e.target.value }))} rows={2} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowConferma(false)}>Annulla</Button>
                <Button onClick={handleConferma} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle className="w-4 h-4 mr-1" />Conferma</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
